import { useCallback, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import type { Layer } from '@deck.gl/core';
import {
  DETAIL_ZOOM,
  INITIAL_VIEW,
  MAPBOX_STYLE,
  MAPBOX_TOKEN,
  OVERVIEW_ZOOM,
  TILE_BASE,
  TILE_VERSION,
} from '../config';
import type { SelectedBuilding, TreeProperties } from '../lib/types';

mapboxgl.accessToken = MAPBOX_TOKEN;

const SRC_TREES = 'tf-trees';
const SRC_TREES_OVERVIEW = 'tf-trees-overview';
const SRC_BUILDINGS = 'tf-buildings';
const SRC_BOROUGHS = 'tf-boroughs';

const LYR_BOROUGH_LINE = 'tf-borough-line';
const LYR_TREES_OVERVIEW = 'tf-trees-overview';
const LYR_TREES = 'tf-trees';
const LYR_BUILDINGS_FILL = 'tf-buildings-fill';
const LYR_BUILDINGS_3D = 'tf-buildings-3d';

export interface TreeHit {
  properties: TreeProperties;
  lon: number;
  lat: number;
}

/** Selection accent, shared by trees and buildings so "chosen" reads the same. */
const SELECT_ACCENT = '#e8b54a';

// Resting paint for the tree circles. Extracted so the selected state can fall
// back to exactly this rather than an approximation of it.
const TREE_FILL = [
  'case',
  ['==', ['get', 'has_canopy'], 0],
  'rgba(150, 158, 154, 0.10)',
  'rgba(107, 196, 58, 0.16)',
] as unknown as mapboxgl.Expression;

const TREE_STROKE = [
  'case',
  ['==', ['get', 'has_canopy'], 0],
  '#8d9691',
  '#6bc43a',
] as unknown as mapboxgl.Expression;

const TREE_STROKE_WIDTH = [
  'interpolate',
  ['linear'],
  ['zoom'],
  OVERVIEW_ZOOM, 0.7,
  17, 1.3,
  22, 4,
] as unknown as mapboxgl.Expression;

const TREE_STROKE_WIDTH_SELECTED = [
  'interpolate',
  ['linear'],
  ['zoom'],
  OVERVIEW_ZOOM, 1.6,
  17, 2.7,
  22, 6.5,
] as unknown as mapboxgl.Expression;

interface Options {
  onSelectTree(hit: TreeHit): void;
  onSelectBuilding(building: SelectedBuilding): void;
  onHoverTree(hit: TreeHit | null, point: { x: number; y: number } | null): void;
  onZoomChange(zoom: number): void;
  /**
   * Fires after every camera move with what is currently on screen. This is
   * what lets shadow classification run against the whole viewport, and lets
   * canopies load without being clicked.
   */
  onViewportChange(view: ViewportContents): void;
}

export interface ViewportContents {
  zoom: number;
  center: [number, number];
  buildings: SelectedBuilding[];
  /** Trees in view that the archive actually has a point cloud for. */
  trees: TreeHit[];
}

function ringOf(geom: GeoJSON.Geometry): Array<[number, number]> | null {
  if (geom.type === 'Polygon') return geom.coordinates[0] as Array<[number, number]>;
  if (geom.type === 'MultiPolygon') return geom.coordinates[0][0] as Array<[number, number]>;
  return null;
}

export function useTreeFolioMap(options: Options) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const overlayRef = useRef<MapboxOverlay | null>(null);
  const readyRef = useRef(false);

  // Layer state that arrives before the style does, replayed onto the layer as
  // it is added. See setBuildingsVisible.
  const buildingsVisibleRef = useRef(true);

  // Callbacks change every render; keep them in a ref so the map is built once.
  const handlers = useRef(options);
  handlers.current = options;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAPBOX_STYLE,
      center: INITIAL_VIEW.center,
      zoom: INITIAL_VIEW.zoom,
      pitch: INITIAL_VIEW.pitch,
      bearing: INITIAL_VIEW.bearing,
      antialias: true,
      attributionControl: true,
    });
    mapRef.current = map;

    // Zoom buttons earn their space on a mouse and not on a touchscreen, where
    // pinch is faster and the corner is scarce. The compass stays either way:
    // returning a rotated, pitched camera to north-up is the one thing that is
    // genuinely hard to do by gesture.
    const canHover = window.matchMedia('(hover: hover)').matches;
    map.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: true, showZoom: canHover }),
      'bottom-right',
    );

    map.on('load', () => {
      // The custom style ships Mapbox's generic extruded "building" layer,
      // which fights with our own footprints. The 2023 app removed it too.
      if (map.getLayer('building')) map.removeLayer('building');

      map.addSource(SRC_BOROUGHS, {
        type: 'vector',
        tiles: [`${window.location.origin}${TILE_BASE}/boroughs/{z}/{x}/{y}.pbf?v=${TILE_VERSION}`],
        minzoom: 0,
        maxzoom: 12,
      });

      // Two tree tilesets: a dense, attribute-free overview for the city-wide
      // view, and the full interactive set from z13 up. See build-tiles.sh.
      map.addSource(SRC_TREES_OVERVIEW, {
        type: 'vector',
        tiles: [`${window.location.origin}${TILE_BASE}/trees-overview/{z}/{x}/{y}.pbf?v=${TILE_VERSION}`],
        minzoom: 0,
        maxzoom: 13,
      });

      map.addSource(SRC_TREES, {
        type: 'vector',
        tiles: [`${window.location.origin}${TILE_BASE}/trees/{z}/{x}/{y}.pbf?v=${TILE_VERSION}`],
        minzoom: 13,
        maxzoom: 16,
      });

      map.addSource(SRC_BUILDINGS, {
        type: 'vector',
        tiles: [`${window.location.origin}${TILE_BASE}/buildings/{z}/{x}/{y}.pbf?v=${TILE_VERSION}`],
        minzoom: 13,
        maxzoom: 16,
      });

      map.addLayer({
        id: LYR_BOROUGH_LINE,
        type: 'line',
        source: SRC_BOROUGHS,
        'source-layer': 'boroughs',
        maxzoom: OVERVIEW_ZOOM,
        paint: {
          'line-color': 'rgba(233, 240, 236, 0.22)',
          'line-width': 0.8,
        },
      });

      // The city-wide canopy. The 2023 site rendered nothing until z15, so the
      // opening view was an empty basemap and the data appeared to be missing.
      // The basemap is light, so these are drawn as small solid dots in a
      // saturated green -- an earlier attempt at 1px / 42% opacity / blurred
      // was technically present but effectively invisible.
      map.addLayer({
        id: LYR_TREES_OVERVIEW,
        type: 'circle',
        source: SRC_TREES_OVERVIEW,
        'source-layer': 'trees',
        maxzoom: OVERVIEW_ZOOM,
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 0.9,
            10, 1.7,
            12, 2.4,
            OVERVIEW_ZOOM, 3,
          ],
          'circle-color': '#2f8f26',
          'circle-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 0.7,
            10, 0.82,
            OVERVIEW_ZOOM, 0.9,
          ],
        },
      });

      map.addLayer({
        id: LYR_TREES,
        type: 'circle',
        source: SRC_TREES,
        'source-layer': 'trees',
        minzoom: OVERVIEW_ZOOM,
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            OVERVIEW_ZOOM, 2.6,
            15, 4,
            18, 9,
            22, 26,
          ],
          // 17% of the census has no usable point cloud (see
          // pipeline/flag-trees.py). Those render grey and hollow so it is
          // clear up front that clicking them will show nothing -- the 2023
          // site drew every tree identically and let you find out by clicking.
          'circle-color': TREE_FILL,
          'circle-stroke-color': TREE_STROKE,
          'circle-stroke-width': TREE_STROKE_WIDTH,
          'circle-stroke-opacity': [
            'case',
            ['==', ['get', 'has_canopy'], 0],
            0.55,
            1,
          ],
          'circle-pitch-alignment': 'map',
        },
      });

      map.addLayer({
        id: LYR_BUILDINGS_FILL,
        type: 'fill',
        source: SRC_BUILDINGS,
        'source-layer': 'buildings',
        minzoom: DETAIL_ZOOM,
        paint: {
          // Subtle, but present enough that footprints read as clickable.
          'fill-color': '#3d4a52',
          'fill-opacity': 0.14,
          'fill-outline-color': 'rgba(61, 74, 82, 0.5)',
        },
      });

      // Every building is extruded. The 2023 site raised only the footprint you
      // clicked, because selecting was also how you loaded its data; with the
      // footprints already in vector tiles there is no reason to hide the city.
      // Clicking now highlights rather than reveals.
      map.addLayer({
        id: LYR_BUILDINGS_3D,
        type: 'fill-extrusion',
        source: SRC_BUILDINGS,
        'source-layer': 'buildings',
        minzoom: DETAIL_ZOOM,
        layout: { visibility: buildingsVisibleRef.current ? 'visible' : 'none' },
        paint: {
          // The basemap is near-white, so a pale massing color disappears into
          // it. This is warm mid-grey: dark enough to read as form, light
          // enough that canopy points stay legible in front of it.
          'fill-extrusion-color': '#9d9891',
          'fill-extrusion-height': ['/', ['to-number', ['get', 'heightroof']], 3.28],
          'fill-extrusion-opacity': 0.5,
        },
      });

      const overlay = new MapboxOverlay({ interleaved: true, layers: [] });
      map.addControl(overlay as unknown as mapboxgl.IControl);
      overlayRef.current = overlay;

      readyRef.current = true;
      handlers.current.onZoomChange(map.getZoom());

      // Read-only handle for debugging and browser-driven testing. Exposed in
      // production too: it was dev-only, and that meant a defect reproducible
      // on the deployed site could not be inspected there. The Map is reachable
      // from devtools regardless, so this grants nothing new.
      (window as unknown as { __tfMap?: mapboxgl.Map }).__tfMap = map;
    });

    map.on('zoomend', () => handlers.current.onZoomChange(map.getZoom()));

    /**
     * What the last published report said, so an identical one is not published
     * again.
     *
     * The camera settling is not the only thing that raises `idle`. The deck.gl
     * overlay draws interleaved with the basemap, so committing a classification
     * repaints the map, which raises another `idle`, which reports the same
     * viewport, which reclassifies it. The two feed each other and never stop:
     * measured stationary at z17 over Midtown, camera untouched, nothing on
     * screen changing --
     *
     *   idle events in 5s                      148
     *   full canopy reclassifications in 5s    143
     *
     * -- so the classification worker was permanently saturated recomputing an
     * answer it had already given, and every real pan started from a machine
     * that was already busy. This was the performance complaint.
     *
     * The signature is the camera plus the feature ids the query returned, which
     * keeps the case the `idle` hook exists for -- tiles arriving under a camera
     * that has not moved, which does change the answer -- while collapsing the
     * loop, since a repaint changes neither.
     */
    let lastSignature = '';

    // queryRenderedFeatures is only meaningful once tiles for the new camera
    // have actually arrived, so this is driven by idle rather than moveend.
    const reportViewport = () => {
      if (!readyRef.current) return;

      const zoom = map.getZoom();
      const c = map.getCenter();

      // Folded over each accepted feature as it is collected, so identifying the
      // set costs no second pass and no string of every id in view. A collision
      // only means one redundant report, which is what this already tolerates.
      let fold = 0;
      const mix = (n: number) => {
        fold = (Math.imul(fold, 31) + (n | 0)) | 0;
      };

      const buildings: SelectedBuilding[] = [];
      if (map.getLayer(LYR_BUILDINGS_FILL) && zoom >= DETAIL_ZOOM) {
        const seen = new Set<string>();
        for (const f of map.queryRenderedFeatures({ layers: [LYR_BUILDINGS_FILL] })) {
          const props = f.properties as Record<string, unknown>;
          const bin = String(props.bin);
          if (seen.has(bin)) continue;
          const ring = ringOf(f.geometry);
          // A footprint with no height casts no shadow, so it would only add
          // work to the hull index for nothing.
          const height = Number(props.heightroof) || 0;
          if (!ring || height <= 0) continue;
          seen.add(bin);
          mix(Number(bin));
          buildings.push({ bin, footprint: ring, heightRoofFeet: height });
        }
      }

      const trees: TreeHit[] = [];
      if (map.getLayer(LYR_TREES) && zoom >= OVERVIEW_ZOOM) {
        const seen = new Set<number>();
        for (const f of map.queryRenderedFeatures({ layers: [LYR_TREES] })) {
          const props = f.properties as unknown as TreeProperties;
          const id = Number(props.tree_id);
          if (!Number.isFinite(id) || seen.has(id)) continue;
          // Skip the 17% the archive has nothing for; requesting them would
          // just be 404s and grey circles already say so.
          if (props.has_canopy === 0) continue;
          seen.add(id);
          mix(id);
          const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
          trees.push({ properties: props, lon: coords[0], lat: coords[1] });
        }
      }

      // Centre is carried at full precision because the consumer sorts by
      // distance to it, so a sub-metre move does change which trees the cap
      // keeps. Rounding here would pin that ordering to a stale centre.
      const signature = `${zoom}|${c.lng}|${c.lat}|${buildings.length}|${trees.length}|${fold}`;
      if (signature === lastSignature) return;
      lastSignature = signature;

      handlers.current.onViewportChange({
        zoom,
        center: [c.lng, c.lat],
        buildings,
        trees,
      });
    };

    map.on('idle', reportViewport);

    /**
     * One click handler for both layers, with a finger-sized search radius.
     *
     * Two things were wrong with a handler per layer. Tree circles render at
     * 2.6px across at z13 and 9px at z18, and a hit test against exactly those
     * pixels is a hopeless target for a fingertip -- so the search box is
     * padded, generously on touch, and the nearest tree inside it wins. And
     * because every tree sits on top of a building footprint, both handlers
     * used to fire for one click: picking a tree silently toggled the building
     * under it as well, quietly changing what the statistics were measured
     * against. Resolving both layers here makes the tree win outright.
     */
    const TAP_RADIUS = canHover ? 4 : 14;

    map.on('click', (e) => {
      const { x, y } = e.point;
      const box: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        [x - TAP_RADIUS, y - TAP_RADIUS],
        [x + TAP_RADIUS, y + TAP_RADIUS],
      ];

      if (map.getLayer(LYR_TREES)) {
        let best: mapboxgl.MapboxGeoJSONFeature | null = null;
        let bestDistance = Infinity;

        for (const f of map.queryRenderedFeatures(box, { layers: [LYR_TREES] })) {
          const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
          const p = map.project(coords);
          const d = (p.x - x) ** 2 + (p.y - y) ** 2;
          if (d < bestDistance) {
            bestDistance = d;
            best = f;
          }
        }

        if (best) {
          const coords = (best.geometry as GeoJSON.Point).coordinates as [number, number];
          handlers.current.onSelectTree({
            properties: best.properties as unknown as TreeProperties,
            lon: coords[0],
            lat: coords[1],
          });
          return;
        }
      }

      // Buildings are large targets, so they are hit tested at the tap point
      // itself -- padding here would let a tap beside a footprint select it.
      if (!map.getLayer(LYR_BUILDINGS_FILL)) return;
      const f = map.queryRenderedFeatures(e.point, { layers: [LYR_BUILDINGS_FILL] })[0];
      if (!f) return;

      const geom = f.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;
      const ring =
        geom.type === 'MultiPolygon'
          ? (geom.coordinates[0][0] as Array<[number, number]>)
          : (geom.coordinates[0] as Array<[number, number]>);

      const props = f.properties as Record<string, unknown>;
      handlers.current.onSelectBuilding({
        bin: String(props.bin),
        footprint: ring,
        heightRoofFeet: Number(props.heightroof) || 0,
      });
    });

    // Hover is bound only where hover exists. A touchscreen synthesises
    // mousemove/mouseenter from a tap, which left the hover card pinned on
    // screen after every tap with nothing able to dismiss it -- there is no
    // mouseleave coming. Touch gets the tap-driven card in App instead.
    if (canHover) {
      map.on('mousemove', LYR_TREES, (e) => {
        const f = e.features?.[0];
        map.getCanvas().style.cursor = f ? 'pointer' : '';
        if (!f) return;
        const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
        handlers.current.onHoverTree(
          { properties: f.properties as unknown as TreeProperties, lon: coords[0], lat: coords[1] },
          { x: e.point.x, y: e.point.y },
        );
      });

      map.on('mouseleave', LYR_TREES, () => {
        map.getCanvas().style.cursor = '';
        handlers.current.onHoverTree(null, null);
      });

      map.on('mouseenter', LYR_BUILDINGS_FILL, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', LYR_BUILDINGS_FILL, () => {
        map.getCanvas().style.cursor = '';
      });
    }

    return () => {
      readyRef.current = false;
      overlayRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  const setDeckLayers = useCallback((layers: Layer[]) => {
    overlayRef.current?.setProps({ layers });
  }, []);

  /**
   * Highlights the clicked buildings. Every building is already extruded, so
   * this recolors rather than filters -- selection marks which building the
   * statistics are answering about.
   */
  const setSelectedBins = useCallback((bins: string[]) => {
    const map = mapRef.current;
    if (!map || !readyRef.current || !map.getLayer(LYR_BUILDINGS_3D)) return;

    const selected = ['in', ['get', 'bin'], ['literal', bins]] as unknown as mapboxgl.Expression;
    map.setPaintProperty(LYR_BUILDINGS_3D, 'fill-extrusion-color', [
      'case',
      selected,
      SELECT_ACCENT,
      '#9d9891',
    ]);
    map.setPaintProperty(LYR_BUILDINGS_3D, 'fill-extrusion-opacity', bins.length ? 0.66 : 0.5);
  }, []);

  /**
   * Marks which trees the user picked.
   *
   * Without this, isolating a tree is invisible on the basemap: its canopy is
   * the only one left in the point cloud, but every circle underneath still
   * looks identical, so there is nothing saying which one you chose. Selected
   * trees take the accent and a heavier ring; the rest fade back so the picked
   * ones carry the eye.
   */
  const setSelectedTreeIds = useCallback((ids: number[]) => {
    const map = mapRef.current;
    if (!map || !readyRef.current || !map.getLayer(LYR_TREES)) return;

    if (ids.length === 0) {
      map.setPaintProperty(LYR_TREES, 'circle-color', TREE_FILL);
      map.setPaintProperty(LYR_TREES, 'circle-stroke-color', TREE_STROKE);
      map.setPaintProperty(LYR_TREES, 'circle-stroke-width', TREE_STROKE_WIDTH);
      return;
    }

    const selected = ['in', ['get', 'tree_id'], ['literal', ids]] as unknown as mapboxgl.Expression;
    map.setPaintProperty(LYR_TREES, 'circle-color', [
      'case',
      selected,
      'rgba(232, 181, 74, 0.30)',
      'rgba(150, 158, 154, 0.05)',
    ]);
    map.setPaintProperty(LYR_TREES, 'circle-stroke-color', [
      'case',
      selected,
      SELECT_ACCENT,
      'rgba(130, 150, 138, 0.4)',
    ]);
    map.setPaintProperty(LYR_TREES, 'circle-stroke-width', [
      'case',
      selected,
      TREE_STROKE_WIDTH_SELECTED,
      TREE_STROKE_WIDTH,
    ]);
  }, []);

  /**
   * Shows or hides the extruded massing. The flat footprint layer stays put so
   * buildings remain clickable, and hiding is purely visual -- they carry on
   * casting shadows and classifying canopy either way.
   */
  const setBuildingsVisible = useCallback((visible: boolean) => {
    // Recorded whether or not the style is up yet. React sets this on mount,
    // which is well before `load` fires, so a call that could only no-op used
    // to be dropped -- and with the massing now off by default, that left the
    // city extruded under a switch that said it was hidden.
    buildingsVisibleRef.current = visible;

    const map = mapRef.current;
    if (!map || !readyRef.current || !map.getLayer(LYR_BUILDINGS_3D)) return;
    map.setLayoutProperty(LYR_BUILDINGS_3D, 'visibility', visible ? 'visible' : 'none');
  }, []);

  const flyTo = useCallback((center: [number, number], zoom: number) => {
    mapRef.current?.flyTo({ center, zoom, duration: 2200, essential: true });
  }, []);

  return {
    containerRef,
    mapRef,
    setDeckLayers,
    setSelectedBins,
    setSelectedTreeIds,
    setBuildingsVisible,
    flyTo,
  };
}
